import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { AddOutlined } from '@mui/icons-material';
import { Alert, Button, MenuItem } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { ChangeSummary, FormAutocomplete, FormDialog, FormTextField, ValueBadge } from '../../design-system';
import { fixtureRewardWorkers, fixtureRewardsSnapshot } from './fixtures';
import { currentSalaryDescription, rewardsReadError, salaryDescription, type RewardWorkerOption, type RewardsApi } from './model';
import { useAsyncResource } from '../../shared/useAsyncResource';

function dateAfter(days = 0) {
  const value = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

type RewardsCreateActionProps = {
  api: RewardsApi;
  fixtureMode: boolean;
  onCreated: (workerId: string) => void;
  initialWorkerId: string;
  canSearchWorkers: boolean;
  canCreate: boolean;
  children: (trigger: ReactElement<ButtonProps> | undefined) => ReactNode;
};

export function RewardsCreateAction({
  api,
  fixtureMode,
  onCreated,
  initialWorkerId,
  canSearchWorkers,
  canCreate,
  children,
}: RewardsCreateActionProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [workerId, setWorkerId] = useState(initialWorkerId);
  const [selectedWorker, setSelectedWorker] = useState<RewardWorkerOption>();
  const [workerSearch, setWorkerSearch] = useState('');
  const [workerSearchQuery, setWorkerSearchQuery] = useState('');
  const [amount, setAmount] = useState('');
  const [currencyCode, setCurrency] = useState('KRW');
  const [frequency, setFrequency] = useState('ANNUAL');
  const [effectiveDate, setEffectiveDate] = useState(dateAfter());
  const [attempt, setAttempt] = useState<{ createKey: string; submitKey: string; draftId?: string }>();
  const requestLocked = Boolean(attempt);

  useEffect(() => {
    if (open || attempt) return;
    setWorkerId(initialWorkerId);
    setSelectedWorker(undefined);
  }, [attempt, initialWorkerId, open]);

  useEffect(() => {
    if (!open || requestLocked) return;
    const timer = window.setTimeout(() => setWorkerSearchQuery(workerSearch.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [open, requestLocked, workerSearch]);

  const workersResource = useAsyncResource(async () => {
    if (!canCreate || !open || !canSearchWorkers) return [];
    if (fixtureMode) {
      const normalized = workerSearchQuery.toLowerCase();
      return fixtureRewardWorkers.filter((worker) => worker.label.toLowerCase().includes(normalized));
    }
    return api.listWorkers(workerSearchQuery, effectiveDate);
  }, [api, canCreate, canSearchWorkers, effectiveDate, fixtureMode, open, workerSearchQuery]);

  useEffect(() => {
    if (!open || requestLocked || selectedWorker || !workerId) return;
    const initialWorker = workersResource.data?.find((worker) => worker.id === workerId);
    if (initialWorker) setSelectedWorker(initialWorker);
  }, [open, requestLocked, selectedWorker, workerId, workersResource.data]);

  const targetWorkerId = canSearchWorkers ? selectedWorker?.id ?? '' : initialWorkerId.trim();
  const currentRewards = useAsyncResource(async () => {
    if (!open || !targetWorkerId) return undefined;
    const snapshot = fixtureMode ? fixtureRewardsSnapshot : await api.getWorkerRewards(targetWorkerId);
    return { workerId: targetWorkerId, snapshot };
  }, [api, fixtureMode, open, targetWorkerId]);
  const currentSnapshot = currentRewards.data?.workerId === targetWorkerId ? currentRewards.data.snapshot : undefined;

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setError('');
  };

  const save = async () => {
    if (!canCreate || submitting) return;
    if (!attempt && (!currentSnapshot || currentRewards.loading || currentRewards.error)) return;
    if (!targetWorkerId || !amount || !effectiveDate) {
      setError('구성원, 금액, 효력일을 입력해 주세요.');
      return;
    }

    let currentAttempt = attempt;
    setSubmitting(true);
    try {
      if (!fixtureMode) {
        currentAttempt ??= { createKey: crypto.randomUUID(), submitKey: crypto.randomUUID() };
        setAttempt(currentAttempt);
        if (!currentAttempt.draftId) {
          const change = await api.createRewardChange({
            changeType: 'COMPENSATION',
            worker: { id: targetWorkerId },
            effectiveDate,
            compensation: {
              compensationType: 'BASE_SALARY',
              currencyCode,
              payFrequency: frequency,
              amount,
            },
          }, currentAttempt.createKey);
          currentAttempt = { ...currentAttempt, draftId: change.id };
          setAttempt(currentAttempt);
        }
        const draftId = currentAttempt.draftId;
        if (!draftId) throw new Error('생성된 보상 변경 초안 ID를 확인하지 못했습니다.');
        await api.submitRewardChange(draftId, currentAttempt.submitKey);
      }
      setAttempt(undefined);
      setOpen(false);
      setError('');
      onCreated(targetWorkerId);
    } catch (reason) {
      if (currentAttempt && !currentAttempt.draftId && api.isDefinitiveRequestFailure(reason)) setAttempt(undefined);
      setError(reason instanceof Error ? reason.message : '변경 요청을 만들지 못했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const trigger = canCreate ? (
    <Button
      variant="contained"
      startIcon={<AddOutlined />}
      onClick={() => {
        setError('');
        if (!attempt) {
          setWorkerId(initialWorkerId);
          setSelectedWorker(undefined);
          setWorkerSearch('');
          setWorkerSearchQuery('');
        }
        setOpen(true);
      }}
    >
      {attempt ? '승인 요청 계속' : '기본급 변경'}
    </Button>
  ) : undefined;

  return (
    <>
      {children(trigger)}
      <FormDialog
        open={canCreate && open}
        onClose={close}
        onSubmit={save}
        title="기본급 변경 요청"
        help="요청은 승인 전까지 현재 보상 원장을 바꾸지 않습니다."
        cancelLabel={attempt ? '나중에 계속' : '취소'}
        submitLabel={attempt ? '승인 요청 다시 시도' : '승인 요청'}
        busyLabel="승인 요청 중…"
        busy={submitting}
        submitDisabled={!attempt && (!currentSnapshot || currentRewards.loading || Boolean(currentRewards.error))}
      >
        {attempt && <Alert severity="warning">요청 결과를 안전하게 확인할 때까지 입력을 잠갔습니다. 닫았다가 다시 열어도 같은 요청을 이어서 제출합니다.</Alert>}
        {canSearchWorkers ? <>
          <FormAutocomplete
            options={workersResource.data ?? []}
            value={selectedWorker ?? null}
            inputValue={selectedWorker?.label ?? workerSearch}
            loading={workersResource.loading}
            disabled={requestLocked}
            getOptionLabel={(worker) => worker.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={workerSearch.trim() ? '검색 결과가 없습니다.' : '구성원이 없습니다.'}
            loadingText="구성원 검색 중…"
            onInputChange={(_, value, reason) => {
              if (reason === 'input' || reason === 'clear') { setWorkerSearch(value); setSelectedWorker(undefined); setWorkerId(''); }
            }}
            onChange={(_, worker) => {
              setWorkerSearch('');
              setSelectedWorker(worker ?? undefined);
              setWorkerId(worker?.id ?? '');
              setError('');
            }}
            input={{ label: '구성원 검색', placeholder: '이름 또는 사번으로 검색' }}
          />
          {workersResource.error && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={workersResource.reload}>다시 시도</Button>}>구성원 정보를 불러오지 못했습니다. 다시 시도한 뒤 이름으로 선택해 주세요.</Alert>}
        </> : <FormTextField label="대상 구성원" value="현재 로그인한 구성원" disabled helperText="다른 구성원을 선택하려면 구성원 조회 권한이 필요합니다." />}
        <FormTextField
          required
          label="금액"
          value={amount}
          disabled={requestLocked}
          onChange={(event) => setAmount(event.target.value)}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        />
        <FormTextField
          label="통화"
          value={currencyCode}
          disabled={requestLocked}
          onChange={(event) => setCurrency(event.target.value.toUpperCase())}
        />
        <FormTextField select label="지급 주기" value={frequency} disabled={requestLocked} onChange={(event) => setFrequency(event.target.value)}>
          <MenuItem value="ANNUAL"><ValueBadge label="연간" /></MenuItem>
          <MenuItem value="MONTHLY"><ValueBadge label="월간" /></MenuItem>
        </FormTextField>
        <FormTextField
          required
          label="효력일"
          type="date"
          value={effectiveDate}
          disabled={requestLocked}
          onChange={(event) => setEffectiveDate(event.target.value)}
        />
        {currentRewards.error && <Alert severity="error" action={<Button onClick={currentRewards.reload}>다시 시도</Button>}>{rewardsReadError(currentRewards.error)}</Alert>}
        {currentSnapshot && <ChangeSummary items={[
          { id: 'salary', label: '기본급', before: currentSalaryDescription(currentSnapshot), after: salaryDescription(amount, currencyCode, frequency) },
          { id: 'date', label: '적용일', before: '현재 적용 중', after: effectiveDate || '미입력' },
        ]} />}
        {error && <Alert severity="error">{error}</Alert>}
      </FormDialog>
    </>
  );
}
